import { type Location } from '@koala/sdk';
import { useRouter } from 'next/router';
import { getStoreHref } from './utils';
import { genericEventHandler } from '@/analytics/events';
import { EventNames, GlobalEvents } from '@/analytics/events/constants';
import { K_ANALYTICS_CTA_LOCATIONS } from '@/constants/events';
import { CART_MIGRATION_TYPES } from '@/constants/locations';
import { ROUTES } from '@/constants/routes';
import { useDispatch, useSelector } from '@/redux';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import globalActions from '@/redux/global/actions';

/**
 * Switches the diner to another store from the Select Location pop-up (TEAM-13315).
 *
 * A switch is a navigation to the store's menu, the same as the store finder. When a
 * basket exists, the menu page's CartReconciliation offers to move it and, for delivery,
 * re-checks that the new store covers the address. Without a basket nothing re-checks a
 * delivery address on the switch (as with the finder's "view menu" link); coverage is
 * checked when the basket is validated. Nothing else is dispatched here:
 * `conveyanceModeSet` without a `time_wanted` would reset a scheduled order to ASAP,
 * and the fulfillment modal the finder opens for delivery is deprecated.
 *
 * "Current" is the basket's store, falling back to the menu page's store, the same
 * store the pill shows.
 */
export function useSwitchLocation() {
  const dispatch = useDispatch();
  const router = useRouter();
  const webConfig = useSelector((state) => state.app.cmsConfig.webConfig);
  const detailId = useSelector((state) => state.app.locations.detail?.id);
  const { location: currentId } = useSelector(selectConveyance);
  const onStorePage = router.pathname === ROUTES.STORE;

  return (target: Location) => {
    if (onStorePage && target.id === detailId) {
      // Already on this store's menu. If the basket belongs to another store, re-open
      // the existing "update your order" prompt; pushing the same URL would do nothing.
      if (target.id !== currentId) {
        dispatch(globalActions.setCartMigration(CART_MIGRATION_TYPES.MIGRATE));
      }
      return;
    }

    if (!onStorePage && target.id === currentId) {
      return;
    }

    genericEventHandler(GlobalEvents.GENERIC__CTA, {
      name: EventNames.CHANGE_LOCATION,
      details: K_ANALYTICS_CTA_LOCATIONS.LOCATION_PILL_SELECT,
    });

    void router.push(getStoreHref(target, webConfig));
  };
}
